<template>
    <div class="thread-message" :class="{ expanded: isExpanded, collapsed: !isExpanded }">
        <div class="mail-meta" @click="toggleExpand">
            <div class="sender-info">
                <div class="sender-avatar">
                    <i class="material-icons">account_circle</i>
                </div>
                <div class="sender-details">
                    <div class="sender-row">
                        <div class="sender-name">
                            {{ getSenderName(mail.sender) }}
                            <span class="sender-email">&lt;{{ mail.sender }}&gt;</span>
                        </div>
                    </div>
                    <div class="recipient">
                        <span class="label">Alıcı:</span>
                        <span class="value">
                            <span
                                v-for="m of mail.to_list"
                                :key="m"
                                @click="
                                    m === (mail.ref_visitor_id && mail.ref_visitor_id.email) && openContactProfile()
                                "
                                :class="{ 'mail-link': m === (mail.ref_visitor_id && mail.ref_visitor_id.email) }"
                            >
                                {{ m }}
                                <span v-if="!isLastToListEmail(m)">,</span>
                            </span>
                        </span>
                        <a-popover placement="bottom" trigger="hover" :overlayStyle="{ maxWidth: '400px' }" @click.stop>
                            <template #content>
                                <div class="mail-details-popover">
                                    <div class="detail-item">
                                        <div class="detail-label">{{ $t("message.subject") }}</div>
                                        <div class="detail-value">{{ mail.subject }}</div>
                                    </div>
                                    <div class="detail-item">
                                        <div class="detail-label">{{ $t("mail.From") }}:</div>
                                        <div class="detail-value">{{ mail.sender }}</div>
                                    </div>
                                    <div class="detail-item">
                                        <div class="detail-label">{{ $t("mail.To") }}:</div>
                                        <div class="detail-value">{{ mail.to_list.join(", ") }}</div>
                                    </div>
                                    <div class="detail-item" v-if="mail.cc_list && mail.cc_list.length > 0">
                                        <div class="detail-label">CC:</div>
                                        <div class="detail-value">{{ mail.cc_list.join(", ") }}</div>
                                    </div>
                                    <div class="detail-item" v-if="mail.bcc_list && mail.bcc_list.length > 0">
                                        <div class="detail-label">BCC:</div>
                                        <div class="detail-value">{{ mail.bcc_list.join(", ") }}</div>
                                    </div>
                                    <div class="detail-item">
                                        <div class="detail-label">{{ $t("chatbotv2.date") }}:</div>
                                        <div class="detail-value">{{ formatDate(mail.created_at) }}</div>
                                    </div>
                                </div>
                            </template>
                            <i class="material-icons mail-detail-toggle">keyboard_arrow_down</i>
                        </a-popover>
                    </div>
                </div>
            </div>

            <!-- Mail tarihi (aksiyon butonları gizliyken bile gösterilsin) -->
            <div class="mail-date-only" v-if="threadShowsComposeForm">
                {{ formatDate(mail.created_at) }}
            </div>

            <!-- Mail Aksiyonları -->
            <div class="mail-actions" @click.stop v-if="!threadShowsComposeForm">
                <div class="mail-date">{{ formatDate(mail.created_at) }}</div>
                <a-dropdown :trigger="['click']" overlayClassName="custom-mail-dropdown">
                    <template #overlay>
                        <a-menu>
                            <a-menu-item key="ai-sentiment" @click="handleSentimentAnalysis" class="ai-menu-item">
                                <div class="ai-tool-menu-item">
                                    <i class="material-icons">psychology</i>
                                    <span>{{ $t("mail.mailAiAssistant.aiSentimentAnalysis") }}</span>
                                </div>
                            </a-menu-item>
                            <a-menu-item key="ai-summary" @click="handleSummarize" class="ai-menu-item">
                                <div class="ai-tool-menu-item">
                                    <i class="material-icons">summarize</i>
                                    <span>{{ $t("mail.mailAiAssistant.aiCreateSummarized") }}</span>
                                </div>
                            </a-menu-item>
                            <a-menu-divider />
                            <a-menu-item key="reply" @click="handleReply">
                                <i class="material-icons">reply</i>
                                <span>{{ $t("mail.reply") }}</span>
                            </a-menu-item>
                            <a-menu-item key="replyAll" @click="handleReplyAll" v-if="hasMultipleRecipients">
                                <i class="material-icons">reply_all</i>
                                <span>{{ $t("mail.replyAll") }}</span>
                            </a-menu-item>
                            <a-menu-item key="forward" @click="handleForward">
                                <i class="material-icons">forward</i>
                                <span>{{ $t("message.forwardRep") }}</span>
                            </a-menu-item>
                            <a-menu-divider />
                            <a-menu-item key="delete" danger @click="$emit('delete', mail)">
                                <i class="material-icons">delete</i>
                                <span>{{ $t("message.delete") }}</span>
                            </a-menu-item>
                        </a-menu>
                    </template>
                    <div class="action-button" :title="$t('message.greaterThan')">
                        <i class="material-icons" :style="{ color: '#22272d' }">more_vert</i>
                    </div>
                </a-dropdown>
            </div>
        </div>

        <!-- Genişletilmiş durumdayken mail içeriği ve ekleri göster -->
        <div class="mail-content" v-if="isExpanded">
            <!-- Mail İçeriği (iframe ile) -->
            <div class="mail-content-container">
                <iframe
                    ref="mailContentIframe"
                    @load="adjustIframeHeight"
                    class="mail-content-iframe"
                    frameborder="0"
                    sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                ></iframe>
            </div>

            <!-- Ekler Bölümü -->
            <attachment-list v-if="mail.attachments && mail.attachments.length > 0" :attachments="mail.attachments" />
        </div>

        <!-- AI Asistan Modals -->
        <MailAIAssistant
            v-if="showAIAssistant"
            :mode="aiAssistantMode"
            :autoRun="true"
            :useCloseButton="true"
            :mailContent="mailBodyContent"
            :threadMails="[mail]"
            @close="showAIAssistant = false"
        />
    </div>
</template>

<script>
import {mapActions} from "vuex"
import AttachmentList from "./AttachmentList.vue";
import MailAIAssistant from "./MailAIAssistant.vue";

export default {
    name: "ThreadMessage",
    components: {
        AttachmentList,
        MailAIAssistant,
    },
    props: {
        mail: {
            type: Object,
            required: true,
        },
        defaultExpanded: {
            type: Boolean,
            default: false,
        },
        threadShowsComposeForm: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isExpanded: this.defaultExpanded,
            cancels: [], // İnterval ve listener referansları için dizi
            showAIAssistant: false,
            aiAssistantMode: "sentiment",
            mailBodyContent: "",
            processedAttachments: [],
        };
    },
    computed: {
        selectedEmail() {
            return this.$parent.selectedEmail;
        },
        hasMultipleRecipients() {
            const allRecipients = [...(this.mail.to_list || []), ...(this.mail.cc_list || [])].filter(
                (email) => email !== this.selectedEmail.mail
            );
            return allRecipients.length > 1;
        },
    },
    watch: {
        isExpanded(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.initIframe();
                });
            }
        },
        mail: {
            immediate: true,
            handler() {
                this.processAttachments();
            },
        },
    },
    mounted() {
        if (this.isExpanded) {
            this.initIframe();
        }

        // Mail içeriğini başlangıçta hazırla
        this.prepareMailContent();

        // Ekleri işle
        this.processAttachments();
    },
    beforeDestroy() {
        // Event listener'ları temizle
        this.cancels.forEach((cancel) => cancel());
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),
        isLastToListEmail(email) {
            return (
                this.mail.to_list &&
                this.mail.to_list.length > 0 &&
                this.mail.to_list[this.mail.to_list.length - 1] === email
            );
        },
        openContactProfile() {
            const visitorId = this.mail.ref_visitor_id?._id;
            if (visitorId)
                this.addNewWindow({
                    type: "show-visitor",
                    title: this.mail.ref_visitor_id?.fullname,
                    payload: {
                        visitorId,
                    },
                    footer: false,
                });
        },
        // Ekleri işleyen ve cdnUrl ekleyen metod
        processAttachments() {
            if (this.mail.attachments && this.mail.attachments.length > 0) {
                console.log("Original attachments:", JSON.stringify(this.mail.attachments));

                // cdnUrl yoksa ekleme işlemi yap
                this.mail.attachments = this.mail.attachments.map((attachment) => {
                    if (!attachment.cdnUrl && attachment.id) {
                        // Dosya uzantısını al
                        const extension = (attachment.name || "").split(".").pop() || "";
                        // siteId'yi store'dan al
                        const siteId = this.$store.state.site._id;
                        // CDN URL'i oluştur
                        const cdnUrl = attachment.v2Format && attachment.cdnUrl
                            ? attachment.cdnUrl
                            : `https://static.supsis.live/${siteId}/email/attachments/${attachment.id}.${extension}`;
                        return { ...attachment, cdnUrl };
                    }
                    return attachment;
                });

                console.log("Processed attachments:", JSON.stringify(this.mail.attachments));
            }
        },

        initIframe() {
            const iframe = this.$refs.mailContentIframe;
            if (!iframe) return;

            const doc = iframe.contentDocument || iframe.contentWindow.document;
            doc.open();
            doc.write(this.mail.body_html);
            doc.close();
            doc.body.style.margin = "0";
            doc.body.style.padding = "0";
            doc.body.style.height = "max-content";
            doc.body.style.width = "100%";
            doc.body.style.maxWidth = "100%";
            doc.body.style.overflowX = "hidden";
            doc.body.style.fontFamily =
                "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif";
            doc.body.style.fontSize = "14px";
            doc.body.style.lineHeight = "1.6";
            doc.body.style.color = "#333";

            const links = doc.getElementsByTagName("a");
            for (let i = 0; i < links.length; i++) {
                links[i].setAttribute("target", "_blank");
                links[i].setAttribute("rel", "noopener noreferrer");
            }

            this.adjustIframeHeight();
            this.$nextTick(() => this.adjustIframeHeight());

            const loadedFn = () => {
                this.adjustIframeHeight();
            };

            iframe.contentWindow.addEventListener("load", loadedFn);

            const timeout = setTimeout(() => {
                this.adjustIframeHeight();
            }, 200);

            this.cancels.push(() => {
                iframe.contentWindow?.removeEventListener("load", loadedFn);
                clearTimeout(timeout);
            });
        },
        adjustIframeHeight() {
            const iframe = this.$refs.mailContentIframe;
            if (iframe && iframe.contentDocument) {
                iframe.style.height = `${iframe.contentDocument.body.scrollHeight}px`;
            }
        },
        toggleExpand() {
            this.isExpanded = !this.isExpanded;
        },
        formatDate(timestamp) {
            return this.$moment(timestamp).format("DD MMMM YYYY, HH:mm");
        },
        getSenderName(email) {
            // Basit bir örnek: e-posta adresinin @ işaretinden önceki kısmını büyük harfe çevirme
            const name = email.split("@")[0];
            return name.charAt(0).toUpperCase() + name.slice(1);
        },
        handleReply() {
            // Otomatik olarak genişlet
            this.isExpanded = true;

            // ThreadMessage'dan MailThread'e compose açma olayını gönder
            this.$emit("open-compose", {
                type: "reply",
                mail: this.mail,
            });
        },
        handleForward() {
            // Otomatik olarak genişlet
            this.isExpanded = true;

            // ThreadMessage'dan MailThread'e yönlendirme compose açma olayını gönder
            this.$emit("open-compose", {
                type: "forward",
                mail: this.mail,
            });
        },
        handleReplyAll() {
            // Otomatik olarak genişlet
            this.isExpanded = true;

            // ThreadMessage'dan MailThread'e tümünü yanıtlama için compose açma olayını gönder
            this.$emit("open-compose", {
                type: "replyAll",
                mail: this.mail,
            });
        },

        // Mail içeriğini hazırla
        prepareMailContent() {
            // Mail içeriğini HTML'den temizle
            const stripHtmlTags = (input) => {
                if (!input) return "";
                const div = document.createElement("div");
                div.innerHTML = input;
                return div.textContent || div.innerText || "";
            };

            this.mailBodyContent = stripHtmlTags(this.mail.body_html || "");
        },

        // AI Asistan işlemleri
        handleSentimentAnalysis() {
            this.aiAssistantMode = "sentiment";
            this.showAIAssistant = true;
        },

        handleSummarize() {
            this.aiAssistantMode = "summarize";
            this.showAIAssistant = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.thread-message {
    margin-bottom: 24px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: all 0.3s ease;
    position: relative;

    &:last-child {
        margin-bottom: 0;
    }

    &.collapsed {
        background-color: #f9f9f9;

        .mail-meta {
            border-bottom: none;
            cursor: pointer;

            &:hover {
                background-color: #f0f0f0;
            }
        }
    }

    &.expanded {
        .mail-meta {
            background-color: #fff;
            border-bottom: 1px solid #eaeaea;
            cursor: pointer;

            &:hover {
                background-color: #f9f9f9;
            }
        }

        .expand-toggle i {
            transform: rotate(180deg);
        }
    }

    // AI Asistan Modal Stili
    .ai-assistant-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 9999;
        display: flex;
        justify-content: center;
        align-items: center;
        animation: fadeIn 0.2s ease;

        .modal-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            cursor: pointer;
        }

        .modal-content {
            position: relative;
            width: 90%;
            max-width: 900px;
            height: 90vh;
            max-height: 800px;
            background-color: #fff;
            border-radius: 12px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
            animation: zoomIn 0.3s ease;
            overflow: hidden;

            :deep(.mail-ai-assistant) {
                box-shadow: none;
                border-radius: 0;
                height: 100%;
            }
        }
    }

    .mail-meta {
        padding: 16px 24px;
        background-color: #fff;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        transition: background-color 0.2s;
        cursor: pointer;

        .mail-date-only {
            font-size: 13px;
            color: #777;
            margin-top: 8px;
        }

        .sender-info {
            display: flex;
            flex: 1;

            .sender-avatar {
                flex-shrink: 0;
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background-color: #eee;
                color: #666;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 16px;

                i {
                    font-size: 20px;
                }
            }

            .sender-details {
                flex: 1;
                min-width: 0;

                .sender-row {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 4px;
                }

                .sender-name {
                    font-size: 15px;
                    font-weight: 600;
                    color: #333;

                    .sender-email {
                        font-weight: normal;
                        color: #666;
                        margin-left: 4px;
                        font-size: 12px;
                    }
                }

                .recipient {
                    font-size: 13px;
                    display: flex;
                    align-items: center;

                    .label {
                        font-weight: 500;
                        color: #555;
                        min-width: 40px;
                    }

                    .value {
                        color: #666;
                        word-break: break-word;
                        margin-right: 4px;

                        .mail-link {
                            cursor: pointer;
                            color: #22272d;
                            text-decoration: underline;
                        }
                    }

                    .mail-detail-toggle {
                        font-size: 16px;
                        color: #777;
                        cursor: pointer;
                        margin-left: 4px;

                        &:hover {
                            color: #22272d;
                        }
                    }
                }
            }
        }

        .mail-actions {
            display: flex;
            gap: 8px;
            align-items: center;

            .expand-toggle {
                cursor: pointer;
                color: #777;

                i {
                    font-size: 20px;
                    transition: transform 0.2s;
                }

                &:hover {
                    color: #333;
                }
            }

            .mail-date {
                font-size: 13px;
                color: #777;
            }
        }
    }

    .mail-content {
        animation: fadeIn 0.3s ease;
    }

    .mail-content-container {
        padding: 16px 24px;
        background-color: #fff;
        min-height: 100px;
        width: 100%;

        .mail-content-iframe {
            width: 100%;
            min-height: 100px;
            height: auto;
            border: none;
            overflow: hidden;
            transition: height 0.2s ease;
        }
    }

    .reply-compose-form {
        border-top: 1px solid #eaeaea;
        margin-top: 10px;
        border-radius: 0 0 8px 8px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        max-height: 60vh;

        :deep(.mail-compose) {
            box-shadow: none;
            border-radius: 0 0 8px 8px;
            overflow: hidden;
            max-height: 60vh;
        }
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes zoomIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

.action-button {
    width: 32px;
    height: 32px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;
    background-color: transparent;
    transition: background-color 0.2s;

    &.small {
        width: 28px;
        height: 28px;
    }

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }

    span {
        margin-left: 4px;
        font-size: 13px;
    }
}
</style>
